import {Textarea} from "@heroui/input";
import {addToast, Avatar, Card, CardBody, DatePicker, Modal, ModalContent, ModalHeader, SelectItem} from "@heroui/react";
import {Table, TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {Icon} from "@iconify/react";
import {parseDate} from "@internationalized/date";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useNavigate} from '@tanstack/react-router'
import dayjs from "dayjs";
import {uniq} from "lodash";
import React, {useState} from "react";
import img_male from "../../../../assets/img_male.png";
import CourtsAvailabilityView from "../../../../components/CourtsAvailabilityView.tsx";
import PButton from "../../../../components/PButton.tsx";
import PSelect from "../../../../components/PSelect.tsx";
import {BookingStatus, BookingStatusToText} from "../../../../constants.ts";
import Header from "../../../../Header.tsx";
import {useJwtUser} from "../../../../JWTAuthProvider.tsx";
import services from "../../../../services";
import {Booking, CourtAvailability, Player} from "../../../../types";
import {tableFullScreen} from "../../../../user/courts/List.tsx";
import queryKeys from "../../../../utilities/queryKeys.ts";

export const Route = createFileRoute('/_app/clubs/$club_uid/bookings')({
    component: RouteComponent,
})

function RouteComponent() {
    const user = useJwtUser();
    const queryClient = useQueryClient();
    const [startDate, setStartDate] = useState(parseDate(dayjs().format('YYYY-MM-DD')) as any);
    const navigate = useNavigate();

    const bookPopupDefaults = {
        open:              false,
        availabilityCourt: null as CourtAvailability | null,
        timeStart:         null as number | null,
        timeStartStr:      null as string | null,
        selectedMinutes:   0,
        message:           '',
    };
    const [bookPopup, setBookPopup] = useState(bookPopupDefaults);

    const bookingsQuery = useQuery({
        queryKey: queryKeys.booking.getBookings(),
        queryFn:  services.booking.getClubBookings,
        enabled:  !!user?.uid,
    })

    const updateBooking = useMutation({
        mutationFn: services.booking.updateBooking,
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getBookings()})
        }
    })

    function confirmDeleteBooking(params) {
        if (confirm('Are you sure?')) {
            deleteBooking.mutate(params);
        }
    }

    const deleteBooking = useMutation({
        mutationFn: services.booking.deleteBooking,
        onSuccess:  () => {
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getBookings()})
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getAvailableCourts()})
        }
    })

    const blockCourt = useMutation({
        mutationFn: async ({minutes}: any) => services.booking.block({
            court_uid:        bookPopup!.availabilityCourt!.court!.uid,
            start_date:       startDate,
            start_time:       bookPopup.timeStartStr,
            duration_minutes: minutes,
            message:          bookPopup.message,
        }),
        onSuccess(response) {
            if (response.error) {
                addToast({
                    color: "danger",
                    title: response.error,
                });
                return;
            }
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getBookings()})
            queryClient.invalidateQueries({queryKey: queryKeys.booking.getAvailableCourts()});
            setBookPopup({...bookPopupDefaults});
        },
    })

    const startConversation = useMutation({
        mutationFn: async (player: Player) => services.conversations.store(player.uid),
        onSuccess:  (data) => {
            navigate({
                to:     '/messages/',
                search: {
                    uid: data.uid,
                },
            });
        }
    })

    return <div>
        <Header backUrl={`/profiles/${user?.uid}`} />

        <div className={"max-w-6xl w-11/12 mx-auto"}>
            <div className={"mb-2 flex justify-between items-center"}>
                <div className={"text-xl"}>Club Bookings</div>
                <DatePicker className={"flex-0 max-w-64"}
                            label="Date"
                            firstDayOfWeek="mon"
                            value={startDate}
                            showMonthAndYearPickers={true}
                            onChange={(value) => setStartDate(value)}
                />
            </div>

            <div className={"mb-4"}>
                <CourtsAvailabilityView
                    mobileViewEnabled={false}
                    sport={''}
                    startDate={startDate}
                    colorCoded={true}
                    onSlotClick={(availability, slot, timeStr) =>
                        setBookPopup({...bookPopup, availabilityCourt: availability, timeStart: slot, timeStartStr: timeStr, open: true})
                    } />
            </div>

            {!bookingsQuery.data?.bookings.length && <>
                No bookings have been made.
            </>}
            {!!bookingsQuery.data?.bookings.length && <>
                {!!bookingsQuery.data &&
                    <Table classNames={tableFullScreen} isStriped={true}>
                        <TableHeader>
                            <TableColumn>Player</TableColumn>
                            <TableColumn>Court</TableColumn>
                            <TableColumn>Time</TableColumn>
                            <TableColumn>Status</TableColumn>
                        </TableHeader>
                        <TableBody>
                            {bookingsQuery.data.bookings.map((booking: Booking) =>
                                <TableRow key={booking.uid}>
                                    <TableCell>
                                        {booking.owner
                                            ? <div className="flex w-full items-center">
                                                <Avatar className={"mr-2"} src={booking.owner.profile_thumb_url || booking.owner.profile_image_url || img_male} />
                                                <div className={"flex flex-col"}>
                                                    <div className={"text-medium"}>{booking.owner.name} {booking.owner.lastname}</div>
                                                    {booking.phone_number && <a href={"tel:" + booking.phone_number} className={"flex gap-1 items-center p-1"}><Icon icon="material-symbols-light:phone-enabled" className="" /> {booking.phone_number}</a>}
                                                    {booking.owner.uid !== user?.uid && <PButton size={"sm"} onPress={() => startConversation.mutate(booking.owner)}>Send message</PButton>}
                                                </div>
                                            </div>
                                            : <div></div>
                                        }
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex w-full items-center">
                                            <Avatar className={"mr-2"} src={booking.court.thumb_url || booking.court.image_url || booking.court.club.thumb_url || booking.court.club.image_url} />
                                            <div className={"text-medium"}>{booking.court.name}</div>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        {booking.start_date} {booking.start_time}<br />
                                        {(booking.is_custom || booking.message)
                                            ? <div><strong>Message:</strong> <i>{booking.message}</i></div>
                                            : <div>{booking.duration_minutes}min</div>
                                        }
                                        {!!booking.price && <div>
                                            {booking.currency_code} {booking.price}
                                        </div>}
                                    </TableCell>
                                    <TableCell className={"flex gap-2"}>
                                        <PSelect
                                            className="min-w-40"
                                            defaultSelectedKeys={[(String(booking.status) || '')]}
                                            onSelectionChange={(e) => {
                                                if (e.currentKey) {
                                                    updateBooking.mutate({uid: booking.uid, version: booking.version + 1, status: +e.currentKey as any});
                                                }
                                            }}
                                            isDisabled={booking.status === BookingStatus.Blocked || booking.status === BookingStatus.Cancelled}
                                            label=""
                                            aria-label="Status"
                                        >{(uniq([booking.status, BookingStatus.Created, BookingStatus.Approved, BookingStatus.Cancelled]) as any).map((status) =>
                                            <SelectItem key={String(status)}
                                                        textValue={BookingStatusToText(status)}
                                            >{BookingStatusToText(status)}</SelectItem>
                                        )}</PSelect>
                                        {booking.owner_uid === user?.uid && <PButton isIconOnly={true} color={"danger"} onPress={() => confirmDeleteBooking({uid: booking.uid})}><Icon icon="material-symbols-light:delete-outline" className="text-2xl" /></PButton>}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                }
            </>
            }
        </div>

        <Modal isOpen={bookPopup.open} placement={"top"} onOpenChange={() => {
            setBookPopup({...bookPopup, open: false});
        }}>
            <ModalContent>
                {() => (
                    <form onSubmit={e => e.preventDefault()}>
                        <ModalHeader className="flex flex-col gap-1">{bookPopup.availabilityCourt?.court.name}</ModalHeader>

                        <div className={"p-4"}>
                            Booking time: {bookPopup.timeStartStr}<br />
                            Block duration:<br />
                            <div className={"mt-2 grid grid-cols-2 gap-2"}>
                                {Object.entries({30: '30m', 60: '1h', 90: '1h 30m', 120: '2h', 150: '2h 30m', 180: '3h', 210: '3h 30m', 240: '4h', 270: '4h 30m', 300: '5h'})
                                    .map(([minutes, title]) =>
                                        <Card isPressable={true}
                                              className={(bookPopup.selectedMinutes === +minutes) ? 'bg-secondary' : ''}
                                              onPress={() => setBookPopup({...bookPopup, selectedMinutes: +minutes})}
                                        ><CardBody>{title}</CardBody></Card>
                                    )}
                            </div>
                            <div>
                                <Textarea
                                    className="mt-3!"
                                    label={"Booking details"}
                                    placeholder={""}
                                    labelPlacement="outside"
                                    variant="flat"
                                    isRequired={true}
                                    onBlur={(e) => {
                                        setBookPopup({...bookPopup, message: (e.target.value || '')});
                                    }}
                                />
                            </div>
                        </div>

                        <div className={"flex justify-center mb-4"}>
                            <PButton color={"primary"} onPress={() => blockCourt.mutate({minutes: bookPopup.selectedMinutes})}>Block</PButton>
                        </div>
                    </form>
                )}
            </ModalContent>
        </Modal>
    </div>
}
